import { useState, useEffect } from "react";
import FileDropZone from "../CommonComponents/FileDropZone";
import {
    Card,
    Input,
    Label,
    Title,
    Wrapper,
    ProgressWrapper,
    ProgressFill,
    Chip,
} from "../CommonComponents//DataCard";
import MessageModal from "../CommonComponents/MessageModal";
import Button from "../CommonComponents/Button";
import AddAuthorModal from "../AuthorComponents/AddAuthorModal";
import AuthorSelector from "../AuthorComponents/AuthorSelector";
import IllustratorSelector from "../IllustratorComponents/IllustratorSelector";
import AddIllustratorModal from "../IllustratorComponents/AddIllustratorModal";
import BarcodeReader from "../BookComponents/BarcodeReader";
import PublisherSelector from "../PublisherComponents/PublisherSelector";
import AddPublisherModal from "../PublisherComponents/AddPublisherModal";
import BookService from "./../../services/BookService";
import { getAllGenres, addGenre } from "../../services/GenreService";

function AddNewBook() {
    const [genres, setGenres] = useState([]);

    useEffect(() => {
        getAllGenres().then(setGenres).catch(() => {});
    }, []);

    const [form, setForm] = useState({
        name: "",
        genreId: "",
        binding: "",
        keywords: [],
        numOfPages: "",
        note: "",
        shortDescription: "",
        totalCopies: "",
        createdAt: "",
        placeOfPublishment: "",
        yearOfPublishment: "",
        publisherId: "",
        authorIds: [],
        illustratorIds: [],
        barcode: "",
    });

    const [keywordInput, setKeywordInput] = useState("");
    const [message, setMessage] = useState(null);
    const [messageSuccess, setMessageSuccess] = useState("");
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showAuthorModal, setShowAuthorModal] = useState(false);
    const [showPublisherModal, setShowPublisherModal] = useState(false);
    const [showIllustratorModal, setShowIllustratorModal] = useState(false);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [selectedIllustrators, setSelectedIllustrators] = useState([]);
    const [selectedPublisher, setSelectedPublisher] = useState();
    const [coverFile, setCoverFile] = useState(null);
    const [contentFile, setContentFile] = useState(null);
    const [showAddGenre, setShowAddGenre] = useState(false);
    const [newGenreName, setNewGenreName] = useState("");

    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState(null);
    const [step, setStep] = useState(1);


    const requiredStep1 = [
        "name",
        "numOfPages",
        "totalCopies"
    ];
    const requiredStep2 = ["authors"];
    const requiredStep3 = ["barcode"];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const handleKeywordChange = (e) => {
        setKeywordInput(e.target.value);
    };

    const handleAddKeyword = () => {
        if (keywordInput.trim() === "") return;

        setForm((prev) => ({
            ...prev,
            keywords: [...prev.keywords, keywordInput.trim()],
        }));
        setKeywordInput("");
    };

    const handleRemoveKeywords = (index) => {
        setForm((prev) => ({
            ...prev,
            keywords: prev.keywords.filter((_, i) => i !== index),
        }));
    };

    const handleAuthorAdded = (newAuthor) => {
        if (!selectedAuthors.find((a) => a.id === newAuthor.id)) {
            setSelectedAuthors((prev) => [...prev, newAuthor]);
            setForm((prev) => ({
                ...prev,
                authorIds: [...prev.authorIds, newAuthor.id],
            }));
            setErrors((prev) => ({ ...prev, authors: false }));
        }
        setShowAuthorModal(false);
    };

    const handleIllustratorAdded = (newIllustrator) => {
        if (!selectedIllustrators.find((a) => a.id === newIllustrator.id)) {
            setSelectedIllustrators((prev) => [...prev, newIllustrator]);
            setForm((prev) => ({
                ...prev,
                illustratorIds: [...prev.illustratorIds, newIllustrator.id],
            }));
        }
        setShowIllustratorModal(false);
    };

    const handlePublisherAdded = (newPublisher) => {
        setSelectedPublisher(newPublisher);
        setForm((prev) => ({
            ...prev,
            publisherId: newPublisher.id,
        }));
        setShowPublisherModal(false);
    };

    const handleBarcodeChange = (value) => {
        setForm((prev) => ({ ...prev, barcode: value }));
        setErrors((prev) => ({ ...prev, barcode: false }));
    };

    const handleAddGenreInline = async () => {
        if (!newGenreName.trim()) return;
        try {
            const added = await addGenre(newGenreName.trim());
            setGenres((prev) => [...prev, added]);
            setForm((prev) => ({ ...prev, genreId: added.id.toString() }));
            setNewGenreName("");
            setShowAddGenre(false);
        } catch (err) {
            // ignore — management page shows proper errors
        }
    };

    const handleCloseModal = () => {
        setShowMessageModal(false);
    };

    const validateStep = (currentStep) => {
        const newErrors = {};

        const check = (fields) => {
            fields.forEach((f) => {
                const value = form[f];
                if (
                    value === undefined ||
                    value === null ||
                    value.toString().trim() === ""
                ) {
                    newErrors[f] = true;
                }
            });
        };

        if (currentStep === 1) {
            check(requiredStep1);
            if (!form.totalCopies || Number(form.totalCopies) <= 0) {
                newErrors.totalCopies = true;
            }
            if (!form.numOfPages || Number(form.numOfPages) <= 0) {
                newErrors.numOfPages = true;
            }
        }

        if (currentStep === 2) {
            if (selectedAuthors.length === 0) {
                newErrors.authors = true;
            }
        }

        if (currentStep === 3) {
            check(requiredStep3);
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...newErrors }));
            setGlobalError("Molimo ispunite sva obvezna polja.");
            return false;
        }

        setGlobalError(null);
        return true;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep((s) => Math.min(s + 1, 3));
        }
    };

    const prevStep = () => {
        setStep((s) => Math.max(s - 1, 1));
    };

    const handleBookAdded = async () => {
        const ok1 = validateStep(1);
        const ok2 = validateStep(2);
        const ok3 = validateStep(3);

        if (!ok1 || !ok2 || !ok3) {
            setStep(1);
            return;
        }

        try {
            const bookPayload = {
                name: form.name,
                genreId: form.genreId ? Number(form.genreId) : null,
                binding: form.binding,
                shortDescription: form.shortDescription,
                barcode: form.barcode,
                numOfPages: Number(form.numOfPages),
                totalCopies: Number(form.totalCopies),
                placeOfPublishment: form.placeOfPublishment,
                yearOfPublishment: Number(form.yearOfPublishment),
                authorIds: selectedAuthors.map((a) => a.id),
                illustratorIds: selectedIllustrators?.map((i) => i.id) || [],
                publisherId: selectedPublisher?.id || null,
                keywords: form.keywords || [],
                note: form.note || "",
            };

            const formData = new FormData();
            formData.append(
                "data",
                new Blob([JSON.stringify(bookPayload)], { type: "application/json" })
            );
            if (coverFile) formData.append("cover", coverFile);
            if (contentFile) formData.append("content", contentFile);

            const addedBook = await BookService.addBook(formData);

            setForm({
                name: "",
                genreId: "",
                binding: "",
                keywords: [],
                numOfPages: "",
                note: "",
                shortDescription: "",
                totalCopies: "",
                createdAt: "",
                placeOfPublishment: "",
                yearOfPublishment: "",
                authorIds: [],
                illustratorIds: [],
                barcode: "",
                content: null,
                cover: null,
                publisherId: "",
            });
            setSelectedAuthors([]);
            setSelectedIllustrators([]);
            setSelectedPublisher(undefined);
            setCoverFile(null);
            setContentFile(null);
            setErrors({});
            setStep(1);

            setMessage("Knjiga je uspješno dodana!");
            setMessageSuccess(true);
            setShowMessageModal(true);
        } catch (error) {
            console.error("Greška pri dodavanju knjige:", error);
            setMessage(
                error.message || "Došlo je do greške pri dodavanju knjige."
            );
            setMessageSuccess(false);
            setShowMessageModal(true);
        }
    };


    return (
        <Wrapper>
            <Card>
                <MessageModal
                    isOpen={showMessageModal}
                    onClose={handleCloseModal}
                    title="Dodavanje knjige"
                    variant={messageSuccess ? "success" : "error"}
                >
                    {message}
                </MessageModal>

                <Title>Dodavanje nove knjige</Title>

                {globalError && (
                    <p
                        style={{
                            color: "red",
                            textAlign: "center",
                            margin: "0.5rem 0",
                        }}
                    >
                        {globalError}
                    </p>
                )}

                <ProgressWrapper>
                    <ProgressFill percent={(step / 3) * 100} />
                </ProgressWrapper>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    { }
                    {step === 1 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-start",
                                gap: "8px",
                            }}
                        >
                            <Card>
                                <h3>Osnovni podaci</h3>
                                <Input
                                    value={form.name}
                                    name="name"
                                    onChange={handleChange}
                                    placeholder="Unesi naziv knjige*"
                                    $error={!!errors.name}
                                />

                                <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "1rem" }}>
                                    <Input
                                        as="select"
                                        name="genreId"
                                        value={form.genreId}
                                        onChange={handleChange}
                                        style={{ marginBottom: 0, flex: 1 }}
                                    >
                                        <option value="">Odaberi žanr</option>
                                        {genres.map((g) => (
                                            <option key={g.id} value={g.id}>{g.name}</option>
                                        ))}
                                    </Input>
                                    <Button
                                        tekst="+"
                                        akcija={() => setShowAddGenre((v) => !v)}
                                        sirina="fit-content"
                                        style={{ marginBottom: 0 }}
                                    />
                                </div>
                                {showAddGenre && (
                                    <div style={{ display: "flex", gap: "6px", marginBottom: "1rem" }}>
                                        <Input
                                            value={newGenreName}
                                            onChange={(e) => setNewGenreName(e.target.value)}
                                            placeholder="Naziv žanra"
                                            style={{ marginBottom: 0, flex: 1 }}
                                            onKeyDown={(e) => e.key === "Enter" && handleAddGenreInline()}
                                        />
                                        <Button tekst="Dodaj" akcija={handleAddGenreInline} sirina="fit-content" />
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column"
                                    }}
                                >

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Label>Uvez: </Label>

                                        <Label
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="binding"
                                                value="Meki"
                                                checked={form.binding === "Meki"}
                                                onChange={handleChange}
                                            />
                                            Meki
                                        </Label>

                                        <Label
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="binding"
                                                value="Tvrdi"
                                                checked={form.binding === "Tvrdi"}
                                                onChange={handleChange}
                                            />
                                            Tvrdi
                                        </Label>

                                    </div>
                                </div>


                                <Input
                                    type="number"
                                    value={form.numOfPages}
                                    name="numOfPages"
                                    onChange={handleChange}
                                    placeholder="Broj stranica*"
                                    $error={!!errors.numOfPages}
                                />

                                <Input
                                    value={form.note}
                                    name="note"
                                    onChange={handleChange}
                                    placeholder="Bilješke"
                                />

                                <Input
                                    type="number"
                                    value={form.totalCopies}
                                    name="totalCopies"
                                    onChange={handleChange}
                                    placeholder="Broj primjeraka*"
                                    $error={!!errors.totalCopies}
                                />

                                <Label>Ključne riječi:</Label>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: "8px",
                                        alignItems: "center",
                                        marginBottom: "1rem",
                                    }}
                                >
                                    <Input
                                        value={keywordInput}
                                        onChange={handleKeywordChange}
                                        placeholder="Unesi ključnu riječ"
                                        style={{ marginBottom: "0px" }}
                                    />
                                    <Button akcija={handleAddKeyword} tekst={"+"} />
                                </div>

                                {form.keywords.map((kw, index) => (
                                    <Chip key={index}>
                                        {kw}
                                        <button
                                            style={{ marginLeft: "4px" }}
                                            onClick={() => handleRemoveKeywords(index)}
                                            type="button"
                                        >
                                            ✖
                                        </button>
                                    </Chip>
                                ))}

                                <Input
                                    value={form.placeOfPublishment}
                                    name="placeOfPublishment"
                                    onChange={handleChange}
                                    placeholder="Mjesto izdavanja"
                                    $error={!!errors.placeOfPublishment}
                                />

                                <Input
                                    type="number"
                                    name="yearOfPublishment"
                                    value={form.yearOfPublishment}
                                    onChange={handleChange}
                                    placeholder="Godina izdavanja"
                                    $error={!!errors.yearOfPublishment}
                                />
                            </Card>
                        </div>
                    )}

                    { }
                    {step === 2 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-start",
                                gap: "8px",
                            }}
                        >
                            <Card>
                                <h3>Podaci o autorima</h3>

                                <AuthorSelector
                                    selectedAuthors={selectedAuthors}
                                    setSelectedAuthors={setSelectedAuthors}
                                    setShowAuthorModal={setShowAuthorModal}
                                    $error={!!errors.authors}
                                />

                                <IllustratorSelector
                                    selectedIllustrators={selectedIllustrators}
                                    setSelectedIllustrators={setSelectedIllustrators}
                                    setShowIllustratorModal={setShowIllustratorModal}
                                />

                                <PublisherSelector
                                    selectedPublisher={selectedPublisher}
                                    setSelectedPublisher={setSelectedPublisher}
                                    setShowPublisherModal={setShowPublisherModal}
                                />
                            </Card>
                        </div>
                    )}

                    {step === 3 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-start",
                                gap: "8px",
                            }}
                        >
                            <Card>
                                <h3>Detalji knjige</h3>

                                <BarcodeReader
                                    onScan={(code) => {
                                        handleBarcodeChange(code);
                                    }}
                                />

                                <Input
                                    name="barcode"
                                    value={form.barcode || ""}
                                    onChange={(e) => handleBarcodeChange(e.target.value)}
                                    placeholder="Barkod*"
                                    $error={!!errors.barcode}
                                />

                                <Label>PDF sadržaj:</Label>
                                <FileDropZone
                                    accept="application/pdf"
                                    label="Dodaj PDF dokument"
                                    icon="📄"
                                    file={contentFile}
                                    onFile={setContentFile}
                                />

                                <Label>Slika knjige:</Label>
                                <FileDropZone
                                    accept="image/*"
                                    label="Dodaj sliku naslovnice"
                                    icon="🖼️"
                                    file={coverFile}
                                    onFile={setCoverFile}
                                />

                                <Label>Kratki opis:</Label>
                                <Input
                                    type="text"
                                    name="shortDescription"
                                    value={form.shortDescription || ""}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            shortDescription: e.target.value,
                                        }))
                                    }
                                    placeholder="Kratki opis knjige"
                                />
                            </Card>
                        </div>
                    )}
                </div>

                <div
                    style={{
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1rem",
                    }}
                >
                    {step > 1 && (
                        <Button tekst="Nazad" akcija={prevStep} sirina="fit-content" />
                    )}

                    {step < 3 && (
                        <Button tekst="Dalje" akcija={nextStep} sirina="fit-content" />
                    )}

                    {step === 3 && (
                        <Button
                            tekst="Dodaj knjigu"
                            akcija={handleBookAdded}
                        />
                    )}
                </div>
            </Card>

            <AddAuthorModal
                isOpen={showAuthorModal}
                onClose={() => setShowAuthorModal(false)}
                onAuthorAdded={handleAuthorAdded}
            />
            <AddIllustratorModal
                isOpen={showIllustratorModal}
                onClose={() => setShowIllustratorModal(false)}
                onIllustratorAdded={handleIllustratorAdded}
            />
            <AddPublisherModal
                isOpen={showPublisherModal}
                onClose={() => setShowPublisherModal(false)}
                onPublisherAdded={handlePublisherAdded}
            />
        </Wrapper>
    );
}

export default AddNewBook;
